<template>
  <el-card shadow="never" class="daily-review-panel" v-loading="loading">
    <div slot="header" class="panel-header">
      <div>
        <div class="panel-title">每日复核与建议确认</div>
        <div class="panel-subtitle">聊天负责分析与生成建议；页面负责查看、选择、二次确认和回读，不自动改账。</div>
      </div>
      <div class="header-actions">
        <el-tag size="small" type="info"><i class="el-icon-lock" /> 安全状态：确认写入默认关闭</el-tag>
        <el-button size="small" icon="el-icon-refresh" :loading="loading" @click="refresh">刷新复核状态</el-button>
      </div>
    </div>

    <el-alert
      v-if="loadError"
      :title="loadError"
      type="warning"
      :closable="false"
      show-icon
      class="panel-alert"
    />

    <div class="review-status-bar">
      <div class="status-primary">
        <span class="status-ready">{{ planStatusLabel }}</span>
        <span class="status-date">{{ tradeDateLabel }}</span>
      </div>
      <div class="status-items">
        <span class="status-item"><strong class="formal-text">{{ formalReviewCount }}</strong> 正式深度复核</span>
        <span class="status-divider" />
        <span class="status-item"><strong class="temp-text">{{ tempReviewCount }}</strong> 临时轻量扫描</span>
        <span class="status-divider" />
        <span class="status-item"><strong :class="pendingProposalCount ? 'pending-text' : 'muted-strong'">{{ pendingProposalCount }}</strong> 待确认建议</span>
        <span class="status-divider" />
        <span class="status-item"><strong>{{ scope.total_count || 0 }} / {{ scope.stock_total_limit || 100 }}</strong> 当前容量</span>
      </div>
    </div>

    <el-tabs v-model="activeTab" class="review-tabs">
      <el-tab-pane label="今日计划" name="plan">
        <div v-if="plan" class="baseline-list">
          <div class="baseline-row">
            <span class="baseline-label">计划编号</span>
            <code class="full-identifier">{{ plan.plan_id || '—' }}</code>
            <el-button type="text" size="mini" icon="el-icon-document-copy" @click="copyText(plan.plan_id, '计划编号')">复制</el-button>
          </div>
          <div class="baseline-row">
            <span class="baseline-label">快照摘要</span>
            <code class="full-identifier">{{ (plan.snapshot && plan.snapshot.digest) || '—' }}</code>
            <el-button type="text" size="mini" icon="el-icon-document-copy" @click="copyText(plan.snapshot && plan.snapshot.digest, '快照摘要')">复制</el-button>
          </div>
          <div class="baseline-row">
            <span class="baseline-label">Git SHA</span>
            <code class="full-identifier">{{ plan.git_sha || '—' }}</code>
            <el-button type="text" size="mini" icon="el-icon-document-copy" @click="copyText(plan.git_sha, 'Git SHA')">复制</el-button>
          </div>
        </div>

        <el-collapse v-model="expandedPlanSections" class="plan-collapse">
          <el-collapse-item name="formal">
            <template slot="title">
              <span class="collapse-title">正式区 · 每日深度复核</span>
              <el-tag size="mini" type="success">{{ formalTargets.length }}只</el-tag>
            </template>
            <el-table :data="formalTargets" size="mini" border empty-text="暂无正式区股票">
              <el-table-column prop="ts_code" label="代码" width="110" />
              <el-table-column prop="name" label="名称" width="100" />
              <el-table-column label="复核重点" min-width="190">
                <template slot-scope="{ row }">
                  <span>{{ questionSummary(row.required_questions) }}</span>
                </template>
              </el-table-column>
              <el-table-column label="允许建议" min-width="150">
                <template slot-scope="{ row }">
                  {{ recommendationSummary(row.allowed_recommendations) }}
                </template>
              </el-table-column>
            </el-table>
          </el-collapse-item>

          <el-collapse-item name="temp">
            <template slot="title">
              <span class="collapse-title">临时区 · 每日轻量扫描</span>
              <el-tag size="mini">{{ tempTargets.length }}只</el-tag>
            </template>
            <el-table :data="tempTargets" size="mini" border empty-text="暂无临时区股票">
              <el-table-column prop="ts_code" label="代码" width="110" />
              <el-table-column prop="name" label="名称" width="100" />
              <el-table-column label="升级触发" min-width="190">
                <template slot-scope="{ row }">
                  <span>{{ triggerSummary(row.deep_review_triggers) }}</span>
                </template>
              </el-table-column>
              <el-table-column label="允许建议" min-width="150">
                <template slot-scope="{ row }">
                  {{ recommendationSummary(row.allowed_recommendations) }}
                </template>
              </el-table-column>
            </el-table>
          </el-collapse-item>
        </el-collapse>
      </el-tab-pane>

      <el-tab-pane name="changes">
        <span slot="label">建议记录 <el-badge :value="pendingProposalCount" :hidden="pendingProposalCount === 0" /></span>
        <el-table :data="changeSets" size="mini" border empty-text="暂无建议变更记录">
          <el-table-column label="创建时间" width="155">
            <template slot-scope="{ row }">{{ formatTime(row.created_at) }}</template>
          </el-table-column>
          <el-table-column label="变更集" min-width="330">
            <template slot-scope="{ row }">
              <div class="identifier-cell">
                <code class="full-identifier">{{ row.change_set_id || '—' }}</code>
                <el-button type="text" size="mini" icon="el-icon-document-copy" @click="copyText(row.change_set_id, '变更集编号')" />
              </div>
            </template>
          </el-table-column>
          <el-table-column label="状态" width="105">
            <template slot-scope="{ row }">
              <el-tag :type="statusTagType(row.status)" size="mini">{{ statusLabel(row.status) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="建议" width="75">
            <template slot-scope="{ row }">{{ row.proposal_count || 0 }} 项</template>
          </el-table-column>
          <el-table-column label="对象" min-width="175">
            <template slot-scope="{ row }">{{ targetSummary(row.targets) }}</template>
          </el-table-column>
          <el-table-column label="门禁状态" width="175">
            <template slot-scope="{ row }">
              <el-tag :type="gateTagType(row)" size="mini">{{ gateLabel(row) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="105" fixed="right">
            <template slot-scope="{ row }">
              <el-button type="text" size="small" @click="openDetail(row.change_set_id)">查看详情</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-tab-pane>
    </el-tabs>

    <el-dialog
      title="每日复核建议详情"
      :visible.sync="detailVisible"
      width="920px"
      :close-on-click-modal="false"
      append-to-body
    >
      <div v-loading="detailLoading">
        <template v-if="detail">
          <div class="detail-header">
            <div class="detail-header-main">
              <div class="detail-id-row">
                <code class="detail-id">{{ detail.change_set_id }}</code>
                <el-button type="text" size="mini" icon="el-icon-document-copy" @click="copyText(detail.change_set_id, '变更集编号')">复制</el-button>
              </div>
              <div class="detail-trade-date">交易日 {{ detail.trade_date }}</div>
            </div>
            <el-tag :type="statusTagType(detail.status)">{{ statusLabel(detail.status) }}</el-tag>
          </div>

          <el-alert
            v-if="detail.status === 'confirmed'"
            title="该建议已确认，确认时门禁已通过。当前快照或Git基线更新不影响历史确认结果。"
            type="success"
            :closable="false"
            show-icon
            class="detail-alert"
          />
          <el-alert
            v-else-if="!detail.can_confirm && detail.status === 'pending'"
            title="当前建议已因快照、Git或有效期变化而失效，请回到聊天重新生成。"
            type="warning"
            :closable="false"
            show-icon
            class="detail-alert"
          />

          <div class="detail-baseline-grid">
            <div class="detail-baseline-item">
              <span class="baseline-label">确认绑定快照</span>
              <code class="full-identifier">{{ detail.stored_snapshot_digest || '—' }}</code>
              <el-button type="text" size="mini" icon="el-icon-document-copy" @click="copyText(detail.stored_snapshot_digest, '确认绑定快照')">复制</el-button>
            </div>
            <div class="detail-baseline-item">
              <span class="baseline-label">当前快照</span>
              <code class="full-identifier">{{ (detail.current_snapshot && detail.current_snapshot.digest) || '—' }}</code>
              <el-button type="text" size="mini" icon="el-icon-document-copy" @click="copyText(detail.current_snapshot && detail.current_snapshot.digest, '当前快照')">复制</el-button>
            </div>
            <div class="detail-baseline-item">
              <span class="baseline-label">确认绑定Git</span>
              <code class="full-identifier">{{ detail.stored_git_sha || '—' }}</code>
              <el-button type="text" size="mini" icon="el-icon-document-copy" @click="copyText(detail.stored_git_sha, '确认绑定Git SHA')">复制</el-button>
            </div>
            <div class="detail-baseline-item">
              <span class="baseline-label">当前Git</span>
              <code class="full-identifier">{{ detail.current_git_sha || '—' }}</code>
              <el-button type="text" size="mini" icon="el-icon-document-copy" @click="copyText(detail.current_git_sha, '当前Git SHA')">复制</el-button>
            </div>
          </div>

          <el-table
            ref="proposalTable"
            :data="detail.proposals || []"
            row-key="proposal_id"
            size="mini"
            border
            @selection-change="handleProposalSelection"
          >
            <el-table-column type="selection" width="42" :selectable="proposalSelectable" />
            <el-table-column type="expand">
              <template slot-scope="{ row }">
                <div class="proposal-expand">
                  <div class="expand-section">
                    <div class="expand-title">建议理由</div>
                    <div>{{ row.reason || '未提供' }}</div>
                  </div>
                  <div class="expand-section">
                    <div class="expand-title">证据摘要</div>
                    <ul v-if="row.evidence_summary && row.evidence_summary.length">
                      <li v-for="item in row.evidence_summary" :key="item">{{ item }}</li>
                    </ul>
                    <span v-else>未提供</span>
                  </div>
                  <div class="expand-section">
                    <div class="expand-title">失效条件</div>
                    <div>{{ row.invalidation_condition || '待补充' }}</div>
                  </div>
                  <div class="expand-section">
                    <div class="expand-title">中文前后对照</div>
                    <div v-if="row.changed_fields && row.changed_fields.length" class="changed-field-list">
                      <div v-for="field in row.changed_fields" :key="field.field" class="changed-field-row">
                        <span class="field-label">{{ field.label }}</span>
                        <span class="field-before">{{ valueLabel(field.before) }}</span>
                        <i class="el-icon-right change-arrow" />
                        <span class="field-after">{{ valueLabel(field.after) }}</span>
                      </div>
                    </div>
                    <span v-else>无字段变化</span>
                  </div>
                  <div v-if="row.quality_flags && row.quality_flags.length" class="expand-section">
                    <div class="expand-title">质量提示</div>
                    <el-tag v-for="flag in row.quality_flags" :key="flag" size="mini" type="warning" class="quality-tag">{{ flag }}</el-tag>
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="对象" width="180">
              <template slot-scope="{ row }">
                <div>{{ row.ts_code }}</div>
                <div class="muted-text">{{ row.name }}</div>
              </template>
            </el-table-column>
            <el-table-column label="建议动作" width="135">
              <template slot-scope="{ row }">{{ actionLabel(row.recommended_action) }}</template>
            </el-table-column>
            <el-table-column label="风险" width="80">
              <template slot-scope="{ row }">
                <el-tag :type="riskTagType(row.risk_level)" size="mini">{{ riskLabel(row.risk_level) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="状态" width="95">
              <template slot-scope="{ row }">
                <el-tag :type="statusTagType(row.status)" size="mini">{{ statusLabel(row.status) }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="reason" label="摘要" min-width="260" show-overflow-tooltip />
            <el-table-column label="到期" width="155">
              <template slot-scope="{ row }">{{ formatTime(row.expires_at) }}</template>
            </el-table-column>
          </el-table>

          <div v-if="detail.status === 'pending'" class="confirmation-panel">
            <div class="confirmation-title">二次确认材料</div>
            <template v-if="selectedConfirmationOption">
              <div class="confirmation-line">已选择 {{ selectedConfirmationOption.proposal_ids.length }} 项；后端将再次校验快照、Git、精确选择和有效期。</div>
              <div class="selection-digest-row">
                <span class="baseline-label">选择摘要</span>
                <code class="full-identifier">{{ selectedConfirmationOption.selection_digest }}</code>
                <el-button type="text" size="mini" icon="el-icon-document-copy" @click="copyText(selectedConfirmationOption.selection_digest, '选择摘要')">复制</el-button>
              </div>
            </template>
            <div v-else class="confirmation-warning">
              请选择“全部普通风险建议”，或只选择一条高风险移出建议；不能混合确认。
            </div>
          </div>

          <div v-if="readback" class="readback-panel">
            <div class="confirmation-title">独立回读</div>
            <div>已确认 {{ readback.confirmed_count || 0 }} 项 · 全部匹配：{{ readback.all_confirmed_rows_match ? '是' : '否' }}</div>
            <div class="selection-digest-row">
              <span class="baseline-label">当前快照</span>
              <code class="full-identifier">{{ (readback.current_snapshot && readback.current_snapshot.digest) || '—' }}</code>
              <el-button type="text" size="mini" icon="el-icon-document-copy" @click="copyText(readback.current_snapshot && readback.current_snapshot.digest, '回读快照')">复制</el-button>
            </div>
          </div>
        </template>
      </div>

      <span slot="footer">
        <el-button @click="detailVisible = false">关闭</el-button>
        <el-button
          v-if="detail && detail.status === 'confirmed'"
          :loading="readbackLoading"
          @click="loadReadback(detail.change_set_id)"
        >重新回读</el-button>
        <el-button
          v-if="detail && detail.status === 'pending'"
          type="primary"
          :disabled="!detail.can_confirm || !selectedConfirmationOption"
          :loading="confirming"
          @click="confirmSelected"
        >二次确认写入</el-button>
      </span>
    </el-dialog>
  </el-card>
</template>

<script>
import { firstBookP0Api, firstBookErrorMessage } from '@/api/firstBookP0'

export default {
  name: 'FirstBookDailyReviewPanel',
  data() {
    return {
      activeTab: 'plan',
      expandedPlanSections: [],
      loading: false,
      loadError: '',
      plan: null,
      changeSets: [],
      detailVisible: false,
      detailLoading: false,
      detail: null,
      selectedProposals: [],
      confirming: false,
      readbackLoading: false,
      readback: null
    }
  },
  computed: {
    scope() {
      return (this.plan && this.plan.current_scope) || {}
    },
    formalTargets() {
      return (this.plan && this.plan.formal_targets) || []
    },
    tempTargets() {
      return (this.plan && this.plan.temp_targets) || []
    },
    formalReviewCount() {
      const workload = (this.plan && this.plan.analysis_workload) || {}
      return Number(workload.formal_deep_review_count || 0)
    },
    tempReviewCount() {
      const workload = (this.plan && this.plan.analysis_workload) || {}
      return Number(workload.temp_light_scan_count || 0)
    },
    pendingProposalCount() {
      return this.changeSets.reduce((sum, item) => {
        const counts = item.status_counts || {}
        return sum + Number(counts.pending || 0)
      }, 0)
    },
    planStatusLabel() {
      if (!this.plan) return '未加载'
      return this.plan.status === 'ok' ? '计划就绪' : '需要处理'
    },
    tradeDateLabel() {
      return (this.plan && this.plan.trade_date) || '等待读取'
    },
    selectedConfirmationOption() {
      if (!this.detail || !this.detail.confirmation_options) return null
      const selected = this.selectedProposals.map(item => item.proposal_id).filter(Boolean).sort()
      if (!selected.length) return null
      return this.detail.confirmation_options.find(option => {
        const ids = (option.proposal_ids || []).slice().sort()
        return ids.length === selected.length && ids.every((value, index) => value === selected[index])
      }) || null
    }
  },
  created() {
    this.refresh()
  },
  methods: {
    async refresh() {
      this.loading = true
      this.loadError = ''
      try {
        const responses = await Promise.all([
          firstBookP0Api.getDailyReviewPlan(),
          firstBookP0Api.listDailyReviewChangeSets(20)
        ])
        this.plan = responses[0] || null
        this.changeSets = (responses[1] && responses[1].items) || []
      } catch (error) {
        this.loadError = firstBookErrorMessage(error, '每日复核页面接口暂不可用')
      } finally {
        this.loading = false
      }
    },
    async openDetail(changeSetId) {
      this.detailVisible = true
      this.detailLoading = true
      this.detail = null
      this.selectedProposals = []
      this.readback = null
      try {
        this.detail = await firstBookP0Api.getDailyReviewChangeSet(changeSetId)
        this.$nextTick(() => this.selectDefaultConfirmationOption())
      } catch (error) {
        this.$message.error(firstBookErrorMessage(error, '读取建议详情失败'))
      } finally {
        this.detailLoading = false
      }
    },
    selectDefaultConfirmationOption() {
      if (!this.detail || !this.detail.can_confirm || !this.$refs.proposalTable) return
      const option = (this.detail.confirmation_options || []).find(item => item.selector === 'all_non_high_risk')
      if (!option) return
      const ids = new Set(option.proposal_ids || [])
      ;(this.detail.proposals || []).forEach(row => {
        if (ids.has(row.proposal_id)) this.$refs.proposalTable.toggleRowSelection(row, true)
      })
    },
    proposalSelectable(row) {
      return Boolean(row && row.status === 'pending')
    },
    handleProposalSelection(rows) {
      this.selectedProposals = rows || []
    },
    async confirmSelected() {
      const option = this.selectedConfirmationOption
      if (!option || !this.detail) return
      const highRisk = option.risk_level === 'high'
      const requiredText = highRisk ? '确认高风险移出' : '确认写入'
      try {
        await this.$prompt(
          highRisk
            ? '这是高风险成员移出操作。请再次核对对象，并输入“确认高风险移出”。'
            : '确认后将只写入当前精确选择，并立即回读。请输入“确认写入”。',
          '第二次确认',
          {
            confirmButtonText: '继续执行',
            cancelButtonText: '取消',
            inputPlaceholder: requiredText,
            inputValidator: value => value === requiredText || `请输入“${requiredText}”`
          }
        )
      } catch (error) {
        return
      }

      this.confirming = true
      try {
        const result = await firstBookP0Api.confirmDailyReviewChangeSet(this.detail.change_set_id, {
          proposal_ids: option.proposal_ids,
          decision: 'confirm',
          expected_snapshot_digest: this.detail.stored_snapshot_digest,
          expected_git_sha: this.detail.stored_git_sha,
          expected_selection_digest: option.selection_digest,
          operator_confirm_phrase: option.operator_confirm_phrase,
          allow_first_book_write_now: true
        })
        const transaction = result.transaction_result || {}
        const summary = [
          `应用 ${Number(transaction.applied || 0)} 项`,
          `新增 ${Number(transaction.created || 0)}`,
          `更新 ${Number(transaction.updated || 0)}`,
          `升区 ${Number(transaction.promoted || 0)}`,
          `降区 ${Number(transaction.demoted || 0)}`,
          `移出 ${Number(transaction.deleted || 0)}`
        ].join('，')
        this.$message.success(`建议已确认：${summary}`)
        this.readback = result.readback || null
        await this.refresh()
        await this.openDetail(result.change_set_id)
        await this.loadReadback(result.change_set_id)
        this.$emit('first-book-changed')
      } catch (error) {
        this.$message.error(firstBookErrorMessage(error, '建议确认失败'))
      } finally {
        this.confirming = false
      }
    },
    async loadReadback(changeSetId) {
      this.readbackLoading = true
      try {
        this.readback = await firstBookP0Api.readbackDailyReviewChangeSet(changeSetId)
        if (this.readback.all_confirmed_rows_match) {
          this.$message.success('独立回读匹配')
        } else {
          this.$message.warning('独立回读存在不匹配，请停止后续操作')
        }
      } catch (error) {
        this.$message.error(firstBookErrorMessage(error, '独立回读失败'))
      } finally {
        this.readbackLoading = false
      }
    },
    gateLabel(row) {
      if (!row) return '未知'
      if (row.status === 'confirmed') return '确认时门禁通过'
      if (row.status === 'pending') {
        return row.snapshot_match && row.git_match && row.can_confirm ? '当前门禁通过' : '当前门禁已失效'
      }
      if (row.status === 'stale_or_expired' || row.status === 'expired') return '当前门禁已失效'
      return '记录已关闭'
    },
    gateTagType(row) {
      if (!row) return 'info'
      if (row.status === 'confirmed') return 'success'
      if (row.status === 'pending') return row.snapshot_match && row.git_match && row.can_confirm ? 'success' : 'danger'
      if (row.status === 'stale_or_expired' || row.status === 'expired') return 'danger'
      return 'info'
    },
    async copyText(value, label = '内容') {
      const text = String(value || '').trim()
      if (!text || text === '—') return
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text)
        } else {
          const textarea = document.createElement('textarea')
          textarea.value = text
          textarea.style.position = 'fixed'
          textarea.style.opacity = '0'
          document.body.appendChild(textarea)
          textarea.select()
          document.execCommand('copy')
          document.body.removeChild(textarea)
        }
        this.$message.success(`${label}已复制`)
      } catch (error) {
        this.$message.warning(`${label}复制失败，请手动选择复制`)
      }
    },
    formatTime(value) {
      if (!value) return '—'
      const raw = String(value)
      const normalized = /Z$|[+-]\d{2}:?\d{2}$/.test(raw) ? raw : `${raw}Z`
      const date = new Date(normalized)
      if (Number.isNaN(date.getTime())) return raw.replace('T', ' ').slice(0, 19)
      return date.toLocaleString('zh-CN', { hour12: false })
    },
    statusLabel(status) {
      return {
        pending: '待确认',
        confirmed: '已确认',
        partially_confirmed: '部分确认',
        stale_or_expired: '已失效',
        rejected: '已拒绝',
        expired: '已过期',
        closed: '已关闭'
      }[status] || status || '未知'
    },
    statusTagType(status) {
      return {
        pending: 'warning',
        confirmed: 'success',
        partially_confirmed: 'warning',
        stale_or_expired: 'danger',
        rejected: 'info',
        expired: 'info',
        closed: 'info'
      }[status] || 'info'
    },
    actionLabel(action) {
      return {
        maintain_current_zone: '维持当前区域',
        update_context: '补充研究上下文',
        update_observation_period: '调整观察周期',
        update_attention_level: '调整关注级别',
        promote_to_formal: '升入正式区',
        upgrade_to_formal: '升入正式区',
        demote_to_temp: '降入临时区',
        downgrade_to_temp: '降入临时区',
        remove_from_first_book: '移出第一本账',
        add_formal: '新增到正式区',
        add_temp: '新增到临时区'
      }[action] || action || '未知建议'
    },
    riskLabel(risk) {
      return { low: '低', medium: '中', high: '高' }[risk] || risk || '未知'
    },
    riskTagType(risk) {
      return { low: 'success', medium: 'warning', high: 'danger' }[risk] || 'info'
    },
    targetSummary(targets) {
      const list = (targets || []).map(item => `${item.ts_code} ${item.name || ''}`.trim())
      if (!list.length) return '—'
      return list.length > 2 ? `${list.slice(0, 2).join('、')} 等${list.length}只` : list.join('、')
    },
    recommendationSummary(items) {
      return (items || []).map(this.actionLabel).join(' / ') || '—'
    },
    questionSummary(items) {
      const labels = {
        research_logic_still_valid: '逻辑有效性',
        market_and_sector_support: '市场/板块支持',
        technical_and_moneyflow_confirmation: '技术与资金确认',
        new_event_or_risk_change: '事件与风险',
        observation_horizon_review: '观察周期',
        attention_level_review: '关注级别',
        maintain_demote_or_remove: '维持/降区/移出'
      }
      return (items || []).slice(0, 4).map(item => labels[item] || item).join('、') || '—'
    },
    triggerSummary(items) {
      const labels = {
        strong_signal: '强信号',
        material_event: '重大事件',
        research_thesis_matured: '研究逻辑成熟',
        unusual_price_volume_moneyflow: '异常价量资金',
        risk_escalation: '风险升级'
      }
      return (items || []).map(item => labels[item] || item).join('、') || '—'
    },
    valueLabel(value) {
      if (value === null || value === undefined || value === '') return '空'
      return String(value)
    }
  }
}
</script>

<style scoped>
.daily-review-panel { margin-bottom: 16px; }
.panel-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
.panel-title { color: #303133; font-size: 17px; font-weight: 700; }
.panel-subtitle { margin-top: 5px; color: #909399; font-size: 12px; }
.header-actions { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.panel-alert { margin-bottom: 12px; }
.review-status-bar { display: flex; align-items: center; justify-content: space-between; gap: 18px; padding: 12px 14px; margin-bottom: 10px; background: #f7f9fc; border: 1px solid #ebeef5; border-radius: 6px; }
.status-primary { display: flex; align-items: baseline; gap: 8px; flex-shrink: 0; }
.status-ready { color: #409eff; font-size: 18px; font-weight: 700; }
.status-date { color: #909399; font-size: 12px; }
.status-items { display: flex; align-items: center; justify-content: flex-end; gap: 12px; flex-wrap: wrap; color: #606266; font-size: 13px; }
.status-item strong { margin-right: 4px; color: #303133; font-size: 16px; }
.status-item .formal-text { color: #67c23a; }
.status-item .temp-text { color: #e6a23c; }
.status-item .pending-text { color: #f56c6c; }
.status-item .muted-strong { color: #909399; }
.status-divider { width: 1px; height: 18px; background: #dcdfe6; }
.review-tabs { margin-top: 2px; }
.baseline-list { display: flex; flex-direction: column; gap: 7px; padding: 10px 12px; margin-bottom: 10px; background: #fafafa; border: 1px solid #ebeef5; border-radius: 5px; }
.baseline-row, .detail-baseline-item, .selection-digest-row { display: grid; grid-template-columns: 92px minmax(0, 1fr) auto; align-items: start; gap: 8px; }
.baseline-label { color: #909399; font-size: 12px; line-height: 24px; }
.full-identifier { color: #606266; font-family: Consolas, Monaco, monospace; font-size: 12px; line-height: 24px; white-space: normal; word-break: break-all; }
.plan-collapse { margin-bottom: 4px; }
.collapse-title { margin-right: 8px; color: #606266; font-size: 13px; font-weight: 700; }
.identifier-cell { display: flex; align-items: flex-start; gap: 5px; }
.detail-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 12px; }
.detail-header-main { min-width: 0; flex: 1; }
.detail-id-row { display: flex; align-items: flex-start; gap: 6px; }
.detail-id { color: #303133; font-family: Consolas, Monaco, monospace; font-size: 15px; font-weight: 700; line-height: 24px; word-break: break-all; }
.detail-trade-date { margin-top: 4px; color: #909399; font-size: 12px; }
.detail-alert { margin-bottom: 12px; }
.detail-baseline-grid { display: grid; grid-template-columns: 1fr; gap: 7px; padding: 10px 12px; margin-bottom: 12px; background: #fafafa; border: 1px solid #ebeef5; border-radius: 5px; }
.muted-text { color: #909399; font-size: 12px; }
.proposal-expand { padding: 4px 10px 8px; color: #606266; line-height: 1.6; }
.expand-section { margin-bottom: 12px; }
.expand-section:last-child { margin-bottom: 0; }
.expand-title { margin-bottom: 4px; color: #303133; font-weight: 700; }
.expand-section ul { margin: 0; padding-left: 20px; }
.changed-field-list { display: flex; flex-direction: column; gap: 7px; }
.changed-field-row { display: grid; grid-template-columns: 110px minmax(0, 1fr) 24px minmax(0, 1fr); align-items: start; gap: 8px; }
.field-label { color: #606266; font-weight: 600; }
.field-before, .field-after { padding: 5px 7px; border-radius: 4px; word-break: break-word; }
.field-before { background: #f5f7fa; color: #909399; }
.field-after { background: #f0f9eb; color: #3c763d; }
.change-arrow { margin-top: 7px; color: #909399; text-align: center; }
.quality-tag { margin-left: 5px; }
.confirmation-panel, .readback-panel { margin-top: 14px; padding: 12px; background: #f7f9fc; border: 1px solid #ebeef5; border-radius: 6px; }
.confirmation-title { margin-bottom: 6px; color: #303133; font-weight: 700; }
.confirmation-line { color: #606266; line-height: 1.6; }
.confirmation-warning { color: #e6a23c; }
.selection-digest-row { margin-top: 7px; }
@media (max-width: 900px) {
  .panel-header { flex-direction: column; }
  .header-actions { width: 100%; justify-content: space-between; }
  .review-status-bar { align-items: flex-start; flex-direction: column; }
  .status-items { justify-content: flex-start; }
  .status-divider { display: none; }
  .baseline-row, .detail-baseline-item, .selection-digest-row { grid-template-columns: 1fr; gap: 2px; }
  .baseline-label { line-height: 20px; }
  .changed-field-row { grid-template-columns: 1fr; }
  .change-arrow { transform: rotate(90deg); }
}
</style>
